import { useSyncExternalStore } from 'react'

/**
 * On phones the document top bar owns the page's single ⋮ menu. The grid's
 * toolbar overflow renders its entries into this slot (a portal target inside
 * that menu) instead of drawing a second ⋮ button of its own.
 *
 * `hosted` says a top bar offering the slot is mounted, so the grid knows it can
 * hoist; `slot` is the element, present only while the menu is open.
 */
type State = { hosted: boolean; slot: HTMLElement | null; close: () => void }

let state: State = { hosted: false, slot: null, close: () => {} }
const listeners = new Set<() => void>()

function set(next: Partial<State>) {
  state = { ...state, ...next }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function setTopBarMenuHosted(hosted: boolean) {
  if (state.hosted !== hosted) set({ hosted })
}

export function setTopBarMenuSlot(slot: HTMLElement | null, close: () => void) {
  if (state.slot !== slot) set({ slot, close })
}

export function closeTopBarMenu() {
  state.close()
}

export function useTopBarMenuHosted(): boolean {
  return useSyncExternalStore(subscribe, () => state.hosted, () => false)
}

export function useTopBarMenuSlot(): HTMLElement | null {
  return useSyncExternalStore(subscribe, () => state.slot, () => null)
}
